import { ArrowRight } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { tracks } from "@/data/tracks";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const chips = [
  { label: "Lifestyle & Wellness", trackId: "lifestyle-wellness" },
  { label: "Technology", trackId: "technology" },
  { label: "Business", trackId: "business" },
  { label: "Creativity", trackId: "creativity" },
  { label: "Community", trackId: "community" },
  { label: "Learning", trackId: "learning" },
];

export default function Hero() {
  return (
    <section
      id="home"
      className="relative overflow-hidden pt-10 md:pt-16 pb-20 md:pb-28"
    >
      {/* Ambient aura gradients */}
      <div
        className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(142,170,156,0.55), transparent 60%)",
        }}
      />
      <div
        className="absolute top-20 -right-32 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(181,166,201,0.55), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--aura-teal)]/20 bg-white/60 backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--aura-teal)] animate-pulse" />
            <span className="eyebrow !text-[var(--aura-teal)]">
              A multi-genre event brand
            </span>
          </div>

          <h1 className="font-display text-[2.75rem] sm:text-6xl lg:text-[4.25rem] leading-[1.02] tracking-tight mt-6 text-[var(--aura-charcoal)]">
            Curated experiences for{" "}
            <span className="italic text-[var(--aura-teal)]">modern living</span>
            , working, learning, and connecting.
          </h1>

          <p className="mt-6 max-w-xl font-body text-base md:text-lg text-[var(--aura-charcoal-2)] leading-relaxed">
            Aura Events brings people together through thoughtfully designed
            experiences across lifestyle, wellness, technology, business,
            creativity, culture, and community.
          </p>

          <p className="mt-3 max-w-xl font-body text-sm md:text-base text-[var(--aura-muted)]">
            Launching with lifestyle and wellness experiences for working
            professionals — expanding into tech, learning, creator, networking,
            and cultural events.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => scrollTo("events")}
              data-testid={HERO.primaryCta}
              className="inline-flex items-center justify-center gap-2 font-ui text-sm md:text-base px-6 py-3.5 rounded-full bg-[var(--aura-teal)] text-[var(--aura-ivory)] hover:bg-[var(--aura-teal-hover)] transition-all group"
            >
              Explore Upcoming Events
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              onClick={() => scrollTo("partners")}
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center justify-center gap-2 font-ui text-sm md:text-base px-6 py-3.5 rounded-full border border-[var(--aura-charcoal)]/15 text-[var(--aura-charcoal)] hover:border-[var(--aura-charcoal)] transition-all"
            >
              Partner or Host With Aura
            </button>
          </div>

          {/* Floating chips */}
          <div className="mt-10 flex flex-wrap gap-2.5">
            {chips.map((c, i) => (
              <button
                key={c.trackId}
                onClick={() => scrollTo("tracks")}
                className="font-ui text-xs md:text-sm px-3.5 py-2 rounded-full bg-white/70 backdrop-blur border border-black/5 hover:border-[var(--aura-teal)]/40 hover:bg-white transition-all animate-float"
                style={{ animationDelay: `${i * 0.25}s` }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right visual collage */}
        <div className="lg:col-span-5 relative">
          <HeroCollage />

          {/* Featured Track card */}
          <div
            data-testid={HERO.featuredCard}
            className="absolute -bottom-8 -left-4 md:-left-10 max-w-xs bg-white shadow-xl shadow-[var(--aura-teal)]/10 border border-black/5 rounded-2xl p-5"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[var(--aura-sage)]" />
              <span className="eyebrow !text-[var(--aura-teal)]">
                Featured Track
              </span>
            </div>
            <div className="mt-2 font-display text-xl leading-tight">
              Lifestyle & Wellness
            </div>
            <p className="mt-2 font-body text-sm text-[var(--aura-charcoal-2)]">
              Practical sessions for better routines, energy, movement,
              nutrition, and mental relaxation.
            </p>
            <button
              onClick={() => scrollTo("wellness")}
              className="mt-3 inline-flex items-center gap-1.5 font-ui text-sm text-[var(--aura-teal)] link-underline"
            >
              See the track <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroCollage() {
  const img = (src, alt, cls) => (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`object-cover w-full h-full ${cls}`}
    />
  );
  return (
    <div className="relative h-[420px] md:h-[520px] w-full">
      <div className="absolute top-0 right-0 w-[62%] h-[58%] rounded-3xl overflow-hidden border border-black/5 shadow-lg">
        {img(
          "https://images.pexels.com/photos/5793687/pexels-photo-5793687.jpeg",
          "A wellness workshop with professionals gathering thoughtfully",
          "",
        )}
      </div>
      <div className="absolute top-[32%] left-0 w-[52%] h-[42%] rounded-3xl overflow-hidden border border-black/5 shadow-lg">
        {img(
          "https://images.pexels.com/photos/28683722/pexels-photo-28683722.jpeg",
          "A tech meetup with a speaker presenting to an engaged audience",
          "",
        )}
      </div>
      <div className="absolute bottom-0 right-6 w-[46%] h-[38%] rounded-3xl overflow-hidden border border-black/5 shadow-lg">
        {img(
          "https://images.unsplash.com/photo-1531058020387-3be344556be6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBuZXR3b3JraW5nJTIwZXZlbnR8ZW58MHx8fHwxNzgzNDI0MTQ1fDA&ixlib=rb-4.1.0&q=85",
          "A professional networking session at a curated event",
          "",
        )}
      </div>
    </div>
  );
}
